import { Inject, Injectable } from '@angular/core';
import { IConfig } from '../interfaces/config.interface';
import { NotificationProviderService, NotificationProviderTypeEnum } from '../external-interfaces/notification-provider.service';
import { BrandTypeEnum } from '../models/cartDetails/brand-type.enum';
import * as CryptoJS from 'crypto-js';

declare let window: any;

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  public brand: BrandTypeEnum;
  public cartId: string;
  public partyId: string | null;
  public externalScriptsActive: boolean;
  public gtmScriptActive: boolean;
  public maxCartValue: { net: number; gross: number };
  public changeDeviceUrl: string;
  public enableMIdCard: boolean;

  private cookieKey = 'configData';
  private secretKey = window['SITE_KEY'];

  constructor(
    @Inject('Endpoint') private endpoint: any,
    private notificationProvider: NotificationProviderService
  ) { }

  public init(): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      const configFromCookie = this.getConfigFromCookie();
      const cartIdFromHelper = window.HELPER_DATA.cartId;

      if (window.STEP !== 1 && configFromCookie && configFromCookie.cartId === cartIdFromHelper) {
        this.applyConfig(configFromCookie);
        resolve();
      } else {
        this.endpoint('newCart.config')
          .run()
          .then((response: IConfig) => {
            this.applyConfig(response);
            this.saveConfigInCookie(response);
            resolve();
          })
          .catch((error: any) => {
            console.error("Can't get cart config", error);
            reject(error);
          });
      }
    });
  }

  private applyConfig(config: IConfig): void {
    this.brand = config.brand;
    this.cartId = config.cartId;
    this.partyId = config.partyId;
    this.externalScriptsActive = window.HELPER_DATA.externalScriptsActive;
    this.gtmScriptActive = window.HELPER_DATA.gtm_scripts_active;
    this.maxCartValue = config.maxCartValue;
    this.changeDeviceUrl = config.changeDeviceUrl;
    this.enableMIdCard = config.enableMIdCard;

    this.notificationProvider.publish(NotificationProviderTypeEnum.CONFIG, {
      brand: config.brand,
      cartId: config.cartId,
      partyId: config.partyId,
      externalScriptsActive: window.HELPER_DATA.externalScriptsActive,
      gtmScriptActive: window.HELPER_DATA.gtm_scripts_active,
      maxCartValue: config.maxCartValue,
      changeDeviceUrl: config.changeDeviceUrl,
      enableMIdCard: config.enableMIdCard
    });
  }

  private saveConfigInCookie(config: IConfig): void {
    const configString = JSON.stringify(config);
    const encryptedConfig = CryptoJS.AES.encrypt(configString, this.secretKey).toString();
    const signature = CryptoJS.HmacSHA256(encryptedConfig, this.secretKey).toString();

    const date = new Date();
    date.setTime(date.getTime() + (2 * 60 * 60 * 1000));

    document.cookie = `${this.cookieKey}=${encodeURIComponent(encryptedConfig)};expires=${date.toUTCString()};path=/`;
    document.cookie = `${this.cookieKey}_signature=${signature};expires=${date.toUTCString()};path=/`;
  }

  private isValidURIComponent(value: string): boolean {
    try {
      decodeURIComponent(value);
      return true;
    } catch (error) {
      return false;
    }
  }

  private getConfigFromCookie(): IConfig | null {
    if (!this.isValidURIComponent(document.cookie)) {
      console.warn('Invalid cookie detected. Removing...');
      this.deleteConfigCookie();
      return null;
    }

    const name = `${this.cookieKey}=`;
    const signatureName = `${this.cookieKey}_signature=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');

    let encryptedConfig: string | null = null;
    let signature: string | null = null;

    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(name) === 0) {
        encryptedConfig = cookie.substring(name.length, cookie.length);
      }
      if (cookie.indexOf(signatureName) === 0) {
        signature = cookie.substring(signatureName.length, cookie.length);
      }
    }

    if (encryptedConfig && signature) {
      const validSignature = CryptoJS.HmacSHA256(encryptedConfig, this.secretKey).toString();

      if (validSignature === signature) {
        try {
          const decryptedBytes = CryptoJS.AES.decrypt(encryptedConfig, this.secretKey);
          const decryptedConfig = decryptedBytes.toString(CryptoJS.enc.Utf8);

          if (!decryptedConfig) {
            throw new Error('Decryption failed');
          }

          return JSON.parse(decryptedConfig);
        } catch (error) {
          console.error('Failed to decrypt config cookie', error);
          this.deleteConfigCookie();
        }
      } else {
        console.warn('Invalid cookie signature. Possible tampering detected.');
        this.deleteConfigCookie();
      }
    }

    return null;
  }

  private deleteConfigCookie(): void {
    const date = new Date(0);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${this.cookieKey}=;${expires};path=/`;
    document.cookie = `${this.cookieKey}_signature=;${expires};path=/`;
  }
}
